/**
 * Attachments Preview Component
 * Universal file preview for all supported file types (images, videos, audio, documents)
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { X, FileText, FileAudio, FileVideo, File as FileIcon, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getFileCategory } from "@/features/file-upload/utils/file-utils";
import type { ChatAttachment } from "../types";

interface AttachmentsPreviewProps {
  files: ChatAttachment[];
  onRemove: (index: number) => void;
  onAddMore?: () => void; // 🆕 Daha fazla dosya eklemek için
}

export const AttachmentsPreview: React.FC<AttachmentsPreviewProps> = ({
  files,
  onRemove,
  onAddMore, // 🆕 Daha fazla dosya eklemek için
}) => {
  const { t } = useTranslation();

  if (files.length === 0) return null;

  // Get file category icon
  const getFileIcon = (fileType: string) => {
    const category = getFileCategory(fileType);
    
    const iconProps = "w-5 h-5 sm:w-6 sm:h-6";
    
    switch (category) {
      case "image":
        return null; // Image preview will be shown
      case "video":
        return <FileVideo className={`${iconProps} text-purple-500 dark:text-purple-400`} />;
      case "audio":
        return <FileAudio className={`${iconProps} text-green-500 dark:text-green-400`} />;
      case "document":
        return <FileText className={`${iconProps} text-blue-500 dark:text-blue-400`} />;
      default:
        return <FileIcon className={`${iconProps} text-gray-500 dark:text-gray-400`} />;
    }
  };

  // Get gradient colors based on file type
  const getGradientColors = (fileType: string) => {
    const category = getFileCategory(fileType);
    
    switch (category) {
      case "image":
        return "from-blue-500/20 via-purple-500/20 to-pink-500/20";
      case "video":
        return "from-purple-500/20 via-pink-500/20 to-red-500/20";
      case "audio":
        return "from-green-500/20 via-emerald-500/20 to-teal-500/20";
      case "document":
        return "from-blue-500/20 via-cyan-500/20 to-sky-500/20";
      default:
        return "from-gray-500/20 via-slate-500/20 to-zinc-500/20";
    }
  };

  return (
    <div className="w-full mb-3 p-3 rounded-2xl bg-gradient-to-br from-gray-50/80 to-gray-100/80 dark:from-gray-800/50 dark:to-gray-900/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
            {files.length} {files.length === 1 ? t("common.file", "dosya") : t("common.files", "dosya")}
          </span>
        </div>
      </div>

      {/* Files Grid */}
      <div className="flex flex-wrap gap-3">
        {/* Add More Button - First Item */}
        {onAddMore && (
          <button
            type="button"
            onClick={onAddMore}
            className="relative group w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-300 hover:scale-105 flex items-center justify-center"
          >
            <div className="flex flex-col items-center justify-center gap-1">
              <Plus className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors" />
              <span className="text-[9px] sm:text-[10px] font-medium text-gray-500 dark:text-gray-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors">
                {t("common.add", "Ekle")}
              </span>
            </div>
          </button>
        )}
        
        {/* Existing Files */}
        {files.map((file, index) => {
          const previewUrl = file.url;
          const fileCategory = getFileCategory(file.mimeType);
          const fileName = file.name || `File ${index + 1}`;
          const fileSize = file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : '';
          const fileIcon = getFileIcon(file.mimeType);
          const gradientColors = getGradientColors(file.mimeType);

          return (
            <div key={file.id || index} className="relative group">
              {/* File Container with Gradient Border */}
              <div className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${gradientColors} p-[2px] transition-all duration-300 group-hover:scale-105 group-hover:shadow-lg`}>
                <div className="relative overflow-hidden rounded-[10px] bg-white dark:bg-gray-800">
                  {/* Image Preview */}
                  {fileCategory === "image" && previewUrl ? (
                    <>
                      <img
                        src={previewUrl}
                        alt={fileName}
                        className="w-20 h-20 sm:w-24 sm:h-24 object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                    </>
                  ) : fileCategory === "video" && previewUrl ? (
                    /* Video Preview */
                    <div className="relative w-20 h-20 sm:w-24 sm:h-24">
                      <video
                        src={previewUrl}
                        className="w-full h-full object-cover"
                        muted
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <div className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center">
                          <FileVideo className="w-4 h-4 text-purple-600" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Icon Preview for Audio/Documents */
                    <div className="w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800">
                      {fileIcon}
                    </div>
                  )}
                </div>
              </div>

              {/* Remove Button - Modern Design */}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onRemove(index)}
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 border-2 border-white dark:border-gray-800"
              >
                <X className="h-3 w-3" />
              </Button>

              {/* File Info - Shows on hover */}
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="px-2 py-0.5 rounded-full bg-black/75 backdrop-blur-sm max-w-[90px]">
                  <p className="text-[9px] font-medium text-white truncate">
                    {fileName.length > 12 ? fileName.substring(0, 12) + "..." : fileName}
                  </p>
                  {fileSize && (
                    <p className="text-[8px] text-white/80 text-center">
                      {fileSize}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

